import { useState, useMemo } from "react";
import { BusinessSettingsLayout } from "@/components/business-settings/BusinessSettingsLayout";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Clock, Shield } from "lucide-react";
import { useMyProfile } from "@/hooks/profile";
import { ShiftCodeDialog } from "@/components/shift-codes/ShiftCodeDialog";
import {
  useShiftCodeList,
  useCreateShiftCode,
  useUpdateShiftCode,
  useDeleteShiftCode,
  useHardDeleteShiftCode,
} from "@/hooks/schedule/useShiftCodeManagement";
import type { ShiftCode, ShiftCodeInput } from "@/services/schedule/shiftCodeService";

function ShiftCodeTableRow({
  shiftCode,
  onEdit,
  onDelete,
  formatTime,
  getCategoryBadge,
}: {
  shiftCode: ShiftCode;
  onEdit: (sc: ShiftCode) => void;
  onDelete?: (sc: ShiftCode) => void;
  formatTime: (t: string | null) => string;
  getCategoryBadge: (c: string) => React.ReactNode;
}) {
  return (
    <TableRow>
      <TableCell className="font-mono font-semibold">
        <div className="flex items-center gap-2">
          {shiftCode.code}
          {shiftCode.is_system && (
            <Badge variant="outline" className="text-xs gap-1">
              <Shield className="h-3 w-3" />
              System
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>{shiftCode.name}</TableCell>
      <TableCell>
        {shiftCode.is_split_shift ? (
          <div>
            <div className="flex items-center gap-1 text-sm font-medium">
              <Clock className="h-3 w-3" />
              {shiftCode.split_segments?.length || 0} segments
            </div>
            <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
              {shiftCode.split_segments?.map((seg, i) => (
                <div key={i}>
                  {seg.label && `${seg.label}: `}
                  {formatTime(seg.start_time)} - {formatTime(seg.end_time)}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Clock className="h-3 w-3" />
            {formatTime(shiftCode.start_time)} - {formatTime(shiftCode.end_time)}
          </div>
        )}
      </TableCell>
      <TableCell>
        {shiftCode.default_hours ? `${shiftCode.default_hours}h` : '—'}
      </TableCell>
      <TableCell>{getCategoryBadge(shiftCode.category)}</TableCell>
      <TableCell>
        <div
          className="w-8 h-8 rounded border border-border"
          style={{ backgroundColor: shiftCode.color }}
        />
      </TableCell>
      <TableCell>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(shiftCode)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          {!shiftCode.is_system && onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(shiftCode)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function ShiftCodeManager() {
  const { data: profile } = useMyProfile();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedShiftCode, setSelectedShiftCode] = useState<ShiftCode | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shiftCodeToDelete, setShiftCodeToDelete] = useState<ShiftCode | null>(null);

  const { data: shiftCodes, isLoading } = useShiftCodeList(profile?.company_id);
  const createMutation = useCreateShiftCode();
  const updateMutation = useUpdateShiftCode();
  const deleteMutation = useDeleteShiftCode();
  const hardDeleteMutation = useHardDeleteShiftCode();

  const { systemCodes, customCodes } = useMemo(() => {
    if (!shiftCodes) return { systemCodes: [], customCodes: [] };
    return {
      systemCodes: shiftCodes.filter((sc) => sc.is_system),
      customCodes: shiftCodes.filter((sc) => !sc.is_system),
    };
  }, [shiftCodes]);

  const handleCreate = () => {
    setSelectedShiftCode(null);
    setDialogOpen(true);
  };

  const handleEdit = (shiftCode: ShiftCode) => {
    setSelectedShiftCode(shiftCode);
    setDialogOpen(true);
  };

  const handleDeleteClick = (shiftCode: ShiftCode) => {
    setShiftCodeToDelete(shiftCode);
    setDeleteDialogOpen(true);
  };

  const handleDeactivate = () => {
    if (shiftCodeToDelete) {
      deleteMutation.mutate(shiftCodeToDelete.id);
      setDeleteDialogOpen(false);
      setShiftCodeToDelete(null);
    }
  };

  const handlePermanentDelete = () => {
    if (shiftCodeToDelete) {
      hardDeleteMutation.mutate(shiftCodeToDelete.id);
      setDeleteDialogOpen(false);
      setShiftCodeToDelete(null);
    }
  };

  const handleSubmit = (values: ShiftCodeInput) => {
    if (selectedShiftCode) {
      updateMutation.mutate(
        { id: selectedShiftCode.id, input: values },
        {
          onSuccess: () => {
            setDialogOpen(false);
            setSelectedShiftCode(null);
          },
        }
      );
    } else {
      createMutation.mutate(values, {
        onSuccess: () => {
          setDialogOpen(false);
        },
      });
    }
  };

  const getCategoryBadge = (category: string) => {
    const variants: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
      work: "default",
      work_split_shift: "default",
      off: "secondary",
      break: "outline",
      training: "secondary",
    };
    const labels: Record<string, string> = {
      work: "work",
      work_split_shift: "split shift",
      off: "off",
      break: "break",
      training: "training",
    };
    return <Badge variant={variants[category] || "default"}>{labels[category] || category}</Badge>;
  };

  const formatTime = (time: string | null) => {
    if (!time) return "—";
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const tableHeaders = (
    <TableHeader>
      <TableRow>
        <TableHead className="w-[140px]">Code</TableHead>
        <TableHead>Name</TableHead>
        <TableHead>Times</TableHead>
        <TableHead className="w-[100px]">Hours</TableHead>
        <TableHead>Category</TableHead>
        <TableHead className="w-[80px]">Color</TableHead>
        <TableHead className="w-[120px]">Actions</TableHead>
      </TableRow>
    </TableHeader>
  );

  return (
    <BusinessSettingsLayout
      title="Shift Codes"
      description="Manage shift codes for roster planning"
    >
      <div className="space-y-6">
        {/* System Shift Codes Section */}
        {systemCodes.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-medium text-muted-foreground">System Shift Codes</h3>
            </div>
            <p className="text-xs text-muted-foreground">
              System codes are used internally and cannot be deleted. You can customize their name and color.
            </p>
            <div className="border rounded-lg">
              <Table>
                {tableHeaders}
                <TableBody>
                  {systemCodes.map((sc) => (
                    <ShiftCodeTableRow
                      key={sc.id}
                      shiftCode={sc}
                      onEdit={handleEdit}
                      formatTime={formatTime}
                      getCategoryBadge={getCategoryBadge}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Custom Shift Codes Section */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-sm font-medium">Custom Shift Codes</h3>
              <p className="text-xs text-muted-foreground">
                {customCodes.length} shift code{customCodes.length !== 1 ? 's' : ''}
              </p>
            </div>
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Add Shift Code
            </Button>
          </div>

          {isLoading ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Loading shift codes...</p>
            </div>
          ) : customCodes.length > 0 ? (
            <div className="border rounded-lg">
              <Table>
                {tableHeaders}
                <TableBody>
                  {customCodes.map((sc) => (
                    <ShiftCodeTableRow
                      key={sc.id}
                      shiftCode={sc}
                      onEdit={handleEdit}
                      onDelete={handleDeleteClick}
                      formatTime={formatTime}
                      getCategoryBadge={getCategoryBadge}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 border rounded-lg">
              <p className="text-muted-foreground mb-4">No custom shift codes defined yet</p>
              <Button onClick={handleCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Shift Code
              </Button>
            </div>
          )}
        </div>
      </div>

      <ShiftCodeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmit={handleSubmit}
        shiftCode={selectedShiftCode}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Shift Code</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                What would you like to do with "{shiftCodeToDelete?.name}"?
              </p>
              <div className="space-y-2 text-sm">
                <div>
                  <strong>Deactivate:</strong> Hides the shift code from the roster planner but preserves all existing data.
                </div>
                <div>
                  <strong className="text-destructive">Delete Permanently:</strong> Removes the shift code entirely from the database. This cannot be undone.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={handleDeactivate}
              disabled={deleteMutation.isPending}
            >
              Deactivate
            </Button>
            <Button
              variant="destructive"
              onClick={handlePermanentDelete}
              disabled={hardDeleteMutation.isPending}
            >
              Delete Permanently
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </BusinessSettingsLayout>
  );
}
